import React, { Component } from 'react';
import autoBind from 'react-autobind';
import { connect } from 'react-redux';
import { withTranslation, Trans } from 'react-i18next';
import { fetchLatestTermRevision } from '../../../services/PricingTerm';
import BasicLayout from '../../layouts/BasicLayout';
import CommentsWrapper from '../../common/CommentsWrapper';
import Loading from '../../Loading';
import { Alert, Label } from '@cimpress/react-components';
import TermActions from './TermActions';
import { css } from 'react-emotion';
import TermItems from './TermItems';
import { withSnackbar } from '../../snackbar/Snackbar';
import { selectors as termSelectors, actions as termActions } from '../../../reducers/terms';
import ErrorRobot from '../../common/ErrorRobot';
import { textOverflowEllipsisStyle } from '../../common/CommonCss';
import TermInfo from './TermInfo';
import TermStatus from '../../../constants/termStatus';
import { isTermExpired } from '../../../utilities/dateFunctions';
import { TermType } from '../../../constants/termTypes';
import EditNameButton from './EditNameButton';
import { extractV2KeyInfo, productAgreementUrl } from '../../../utilities/productAgreementHelper';
import { selectors as identitySelectors } from '../../../reducers/identities';
import { getPricingArchVersion } from '../../../../src/services/PricingArchInfo';
import { PRICING_ARCH_V2 } from '../../../constants/priceArchs';

const revisionLabelStyle = css`
  margin-left: 10px;
`;

const bodyStyle = css`
  @media (min-width: 992px) {
    display: flex;
  }
`;

class ViewTermRevisionPage extends Component {
  constructor(props) {
    super(props);
    autoBind(this);
    this.unmounted = false;

    this.state = {
      latestRevisionId: null,
      isLatestRevisionLoading: false,
      isFulfillerMovedToV2: false,
      fulfillerPricingArchApiCall: false
    };
  }

  componentDidMount() {
    const revisionId = this.props.match.params.revisionId;
    const termId = this.props.match.params.id;
    this.props.fetchTermRevision(termId, revisionId);
    this.loadLatestTermRevision();
  }

  componentWillUnmount() {
    this.unmounted = true;
    this.props.resetFetchTermRevisionStatus();
  }

  // TODO: Handle no data skus introductions and scenes
  componentDidUpdate(prevProps, prevState, snapshot) {
    const revisionId = this.props.match.params.revisionId;
    const termId = this.props.match.params.id;

    if (prevProps.match.params.id !== termId || prevProps.match.params.revisionId !== revisionId) {
      this.loadLatestTermRevision();
      this.props.fetchTermRevision(termId, revisionId);
    }

    if (this.props.termRevision && this.props.termRevision.seller) {
      const fulfillerData = this.props.fulfillersById[this.props.termRevision.seller.id];
      if (fulfillerData && this.state.fulfillerPricingArchApiCall === false) {
        this.fetchPricingArchVersionForFulfiller(fulfillerData.accountId);
        this.setState({ fulfillerPricingArchApiCall: true });
      }
    }
  }

  loadLatestTermRevision = () => {
    this.setState({ isLatestRevisionLoading: true });
    fetchLatestTermRevision(this.props.match.params.id).then(lt => {
      this.setState({
        latestRevisionId: lt.revisionId,
        isLatestRevisionLoading: false
      });
    });
  };

  fetchPricingArchVersionForFulfiller = fulfillerAccountId => {
    if (this.state.fulfillerPricingArchApiCall === false) {
      getPricingArchVersion(fulfillerAccountId).then(pricingArchForFulfiller => {
        if (pricingArchForFulfiller === PRICING_ARCH_V2) {
          this.setState({ isFulfillerMovedToV2: true });
        } else {
          this.setState({ isFulfillerMovedToV2: false });
        }
      });
    }
  };

  setLatestRevision(newTerm) {
    if (!this.unmounted) {
      const v2Key = this.props.match.params.v2Key;
      this.props.history.replace(`/${newTerm.termId}/${newTerm.revisionId}/${v2Key}`);
    }
  }

  render() {
    const { t, termRevision, isPageLoading, pageLoadingFailed, priceModels, getSkuRuleSetLoadingStatus } = this.props;
    const { isLatestRevisionLoading, latestRevisionId, isFulfillerMovedToV2 } = this.state;

    if (pageLoadingFailed) {
      return <ErrorRobot title="Nothing here..." />;
    }

    if (isLatestRevisionLoading || isPageLoading) {
      return <Loading />;
    } else {
      const isLatestRevision = latestRevisionId && termRevision && termRevision.revisionId === latestRevisionId;

      const userHasPermissionToEdit =
        termRevision &&
        (termRevision.userHasAuthorizationFor.seller ||
          (termRevision.type === TermType.LIST_PRICE && termRevision.userHasAuthorizationFor.buyer));

      // restrict editing on the term level

      let termIsEditable =
        isLatestRevision &&
        userHasPermissionToEdit &&
        termRevision.status !== TermStatus.TERMINATED &&
        !isTermExpired(termRevision.effectiveEndDate) &&
        !isFulfillerMovedToV2;

      if (
        termRevision &&
        termRevision.type === TermType.SHIPPING_PRICE &&
        isLatestRevision &&
        userHasPermissionToEdit &&
        termRevision.status !== TermStatus.TERMINATED &&
        !isTermExpired(termRevision.effectiveEndDate)
      ) {
        termIsEditable = true;
      }
      const v2Key = this.props.match.params.v2Key;
      const v2Info = extractV2KeyInfo(v2Key);
      return (
        <div>
          <div style={{ paddingLeft: '20px' }}>
            {v2Info.businessIds && v2Info.businessIds !== 'undefined' ? (
              <a href={productAgreementUrl(v2Info)}>Back to Product Agreements</a>
            ) : null}
          </div>
          <BasicLayout className="view-term">
            {getSkuRuleSetLoadingStatus &&
              getSkuRuleSetLoadingStatus.isError && (
                <div>
                  <Alert
                    title="Ruleset failed to load"
                    message="Unable to load Ruleset for the selected SKU."
                    dismissible={true}
                  />
                </div>
              )}
            {isFulfillerMovedToV2 ? (
              <div className="view-term__header">
                <Alert
                  title={t('createContract:getStarted.sellerInPricingArchV2MessageTooltip')}
                  status="warning"
                  message={
                    <div>
                      <span>Please use </span>
                      <a href={process.env.REACT_APP_PRICINGMANAGER_V2_URL}>Pricing Manager V2</a>
                    </div>
                  }
                  dismissible={false}
                  type="warning"
                />
              </div>
            ) : null}
            {termRevision && (
              <div className="view-term__header">
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                  <Label
                    className={revisionLabelStyle}
                    text={`${t('common:commonTerminology.revision')}: ${termRevision.revisionId}`}
                    type="info"
                  />
                  <div style={{ marginLeft: 'auto' }}>
                    {isLatestRevision ? (
                      <TermActions
                        termRevision={termRevision}
                        onStatusUpdated={this.setLatestRevision}
                        buttonStyle="view-term__term-actions"
                        isFulfillerMigrated={isFulfillerMovedToV2}
                      />
                    ) : (
                      <i>
                        <Trans i18nKey="contractInfo:readOnlyRevisionText">
                          This page is read-only since you are not viewing the latest revision.
                          <a href={`/${termRevision.termId}/${latestRevisionId}`}>
                            Click here to view the latest revision
                          </a>
                        </Trans>
                      </i>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex' }}>
                  <span
                    className={`view-term__term-name ${textOverflowEllipsisStyle}`}
                    style={{ paddingLeft: '7px', width: 'auto' }}>
                    {termRevision.additionalData && termRevision.additionalData.name}
                  </span>
                  {termIsEditable && (
                    <EditNameButton termRevision={termRevision} setLatestRevision={this.setLatestRevision} />
                  )}
                </div>
              </div>
            )}
            {termRevision && (
              <div className={`view-term__body ${bodyStyle}`}>
                <div className="col-xs-12">
                  <TermInfo
                    latestRevisionId={latestRevisionId}
                    termRevision={termRevision}
                    isLayoutVertical={false}
                    priceModels={priceModels}
                    editable={termIsEditable}
                    setLatestRevision={this.setLatestRevision}
                    v2Key={v2Key}
                    v2Info={v2Info}
                  />
                </div>
              </div>
            )}
            {termRevision && (
              <div className={`view-term__items ${bodyStyle}`}>
                <div className="col-xs-12">
                  <TermItems
                    termRevision={termRevision}
                    setLatestRevision={this.setLatestRevision}
                    termIsEditable={termIsEditable}
                  />
                </div>
              </div>
            )}
            {termRevision && (
              <div className={`view-term__comments ${bodyStyle}`}>
                <div className="col-xs-12">
                  <CommentsWrapper termId={termRevision.termId} />
                </div>
              </div>
            )}
          </BasicLayout>
        </div>
      );
    }
  }
}

const mapStateToProps = (state, ownProps) => {
  const termId = ownProps.match.params.id;
  const revisionId = ownProps.match.params.revisionId;
  return {
    termRevision: termSelectors.getTermRevisionWithMoreInfo(state, termId, revisionId),
    isPageLoading: termSelectors.getIsViewTermRevisionPageLoading(state),
    pageLoadingFailed: termSelectors.getHasViewTermRevisionPageLoadingFailed(state),
    priceModels: termSelectors.getPriceModelsForTerm(state, termId, revisionId),
    getSkuRuleSetLoadingStatus: termSelectors.getSkuRuleSetLoadingStatus(state),
    fulfillersById: identitySelectors.getFulfillersById(state)
  };
};

const mapDispatchToProps = {
  fetchTermRevision: termActions.fetchTermRevision,
  resetFetchTermRevisionStatus: termActions.resetFetchTermRevisionStatus
};

export default withSnackbar(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(withTranslation()(ViewTermRevisionPage))
);
